import React from 'react';
import {
  FaCashRegister,
  FaClipboardList,
  FaComments,
  FaFileMedical,
  FaNotesMedical,
  FaPaw,
  FaSyringe,
  FaStethoscope,
  FaStore,
  FaWarehouse,
} from 'react-icons/fa';
import ProductPageTemplate from '../../components/productPageTemplate';

const data = {
  theme: {
    accent: '#F3C130',
    accentSoft: '#FFD866',
    darkBg: '#654B00',
    darkBgSoft: '#A07300',
    pageBackground: `
      radial-gradient(circle at left 30%, rgba(243, 193, 48, 0.16) 0%, transparent 26%),
      radial-gradient(circle at right top, rgba(243, 193, 48, 0.18) 0%, transparent 28%),
      linear-gradient(180deg, #fffdf5 0%, #ffffff 58%, #fffef9 100%)
    `,
  },
  hero: {
    eyebrows: ['Pet shop e clínica veterinária', 'Prontuário, agenda e caixa', 'SEUPDV e operação pet'],
    accent: 'Pet',
    title: 'que organiza atendimento, rotina clínica e frente de caixa.',
    lead:
      'O ECOSIS Pet reúne agenda, cadastro do tutor e do pet, mensagens automáticas, prontuário, vacinas, hospedagem e operação comercial em uma base só.',
    support:
      'A proposta é tirar o pet shop e a clínica do improviso, deixando o atendimento mais consistente e a gestão diária mais confiável.',
    metrics: [
      'Agenda, comanda e histórico do pet no mesmo fluxo',
      'Mensagens automáticas e relacionamento contínuo',
      'Operação fiscal exige certificado A1 (.pfx)',
    ],
    primaryCta: { label: 'Ver módulos', href: '#modulos' },
    secondaryCta: { label: 'Falar com a equipe', href: '#contato-produto' },
    visualCards: [
      { label: 'Atendimento pet', value: 'Mais organizado' },
      { label: 'Clínica e varejo', value: 'Mesma base' },
    ],
    panelTitle: 'Painel do atendimento pet',
    panelItems: [
      {
        label: 'Cadastro',
        title: 'Tutor e pet em detalhe',
        meta: 'Espécie, raça, peso, alergias, observações e imagem em um histórico acessível.',
      },
      {
        label: 'Rotina',
        title: 'Agenda com registro completo',
        meta: 'Banho, tosa, consultas, vacinas, exames e comandas ligados ao atendimento.',
      },
      {
        label: 'Clínica',
        title: 'Prontuário, anamnese e vacinas',
        meta: 'Histórico clínico, protocolos, internação e comunicação mais contínua.',
      },
    ],
  },
  intro: {
    tag: 'Visão geral',
    title: 'Uma leitura mais profissional para um produto que mistura clínica, serviço e varejo.',
    lead:
      'A página do Pet já tinha bastante conteúdo, mas faltava hierarquia visual. Agora a narrativa do produto fica mais clara e com menos ruído.',
    cardTitle: 'Tudo o que o pet shop precisa, em um só lugar',
    cardText:
      'A solução foi montada para atender atendimento clínico e operação comercial, conectando agenda, prontuário, vacinas, hospedagem, mensagens e PDV em uma única rotina.',
    quickPoints: [
      'Cadastro rico do tutor e do pet com histórico fácil de consultar.',
      'Agenda com faltas, recorrência, comanda e automação por WhatsApp.',
      'Prontuário, anamnese, vacinas, hospedagem e internação no mesmo sistema.',
    ],
  },
  modulesSection: {
    tag: 'Módulos principais',
    title: 'Os blocos do ECOSIS Pet agora seguem o mesmo padrão visual do restante do portfólio.',
    lead:
      'Todo o conteúdo central da página anterior foi mantido e reorganizado para facilitar entendimento comercial e leitura técnica.',
  },
  modules: [
    {
      icon: FaClipboardList,
      title: 'Cadastro de clientes e pets',
      description: 'Mantenha tutor e pet com dados completos para atendimento e relacionamento mais consistente.',
      bullets: ['Tutor e pet no mesmo cadastro', 'Espécie, raça, peso, alergias e observações', 'Imagem do pet e dos itens cadastrados'],
    },
    {
      icon: FaPaw,
      title: 'Agenda inteligente e atendimentos',
      description: 'Acompanhe agenda por profissional e diferentes tipos de atendimento sem perder histórico do que foi feito.',
      bullets: ['Banho, tosa, consultas, exames e vacinas', 'Agendamentos recorrentes', 'Registro de faltas e comandas'],
    },
    {
      icon: FaComments,
      title: 'WhatsApp e mensagens automáticas',
      description: 'Automatize lembretes e comunicações para reduzir faltas e reforçar o relacionamento com o tutor.',
      bullets: ['Confirmações e lembretes', 'Avisos de vacinas pendentes', 'Variáveis para mensagens personalizadas'],
    },
    {
      icon: FaFileMedical,
      title: 'Prontuário e anamnese',
      description: 'Concentre o histórico clínico do pet com anexos, impressões e visão evolutiva do atendimento.',
      bullets: ['Atendimentos, exames, vacinas e cirurgias', 'Anexos de documentos e imagens', 'Impressão de anamnese e resumos'],
    },
    {
      icon: FaSyringe,
      title: 'Gestão clínica e vacinas',
      description: 'Monitore protocolos de doses e sugira próximas aplicações com mais previsibilidade.',
      bullets: ['Controle de vacinas por protocolo', 'Sugestão das próximas datas', 'Busca rápida por tags de atendimento'],
    },
    {
      icon: FaWarehouse,
      title: 'Hospedagem, internação e operação',
      description: 'Apoie rotinas complementares do negócio sem quebrar a continuidade de gestão dentro do sistema.',
      bullets: ['Reservas, check-in e check-out', 'Controle de internação e evolução', 'Combos de serviços e controle de atendimento'],
    },
    {
      icon: FaCashRegister,
      title: 'SEUPDV e frente de caixa',
      description: 'A operação de balcão continua mesmo sem internet, com sincronização posterior para o sistema em nuvem.',
      bullets: ['PDV offline com emissão de NFCe', 'Leitor de código de barras e atalhos', 'Sincronização de estoque e financeiro'],
    },
    {
      icon: FaStore,
      title: 'Integrações e parceiros',
      description: 'Conecte a rotina do pet shop a pagamentos, comunicação e meios já conhecidos pelo mercado.',
      bullets: ['PIX, TEF e WhatsApp', 'Stone, Vero, Mercado Pago e Cielo', 'Operação comercial conectada à clínica'],
    },
  ],
  operations: {
    tag: 'Onde encaixa melhor',
    title: 'O produto atende a operação clínica e comercial do segmento pet.',
    lead:
      'Esse bloco resume perfis atendidos e reforça os pontos operacionais que mais diferenciam o produto no dia a dia.',
    audienceTitle: 'Perfis atendidos',
    audienceText:
      'O ECOSIS Pet foi pensado para quem precisa cuidar do atendimento, do prontuário e da venda sem manter sistemas separados para cada frente.',
    sectors: [
      { icon: FaPaw, label: 'Pet shop' },
      { icon: FaStethoscope, label: 'Clínica veterinária' },
      { icon: FaNotesMedical, label: 'Atendimento clínico' },
      { icon: FaStore, label: 'Operação de balcão' },
    ],
    stat: {
      value: '360º',
      text: 'O mesmo sistema cobre cadastro, atendimento, vacinação, internação, mensagens e frente de caixa.',
    },
    cards: [
      {
        title: 'Vídeo de apoio: cadastro',
        text: 'A versão anterior trazia material de apoio para cadastrar espécie, raça e estruturar a base do pet.',
        bullets: ['Cadastro mais detalhado', 'Base mais consistente para atendimento'],
        link: {
          href: 'https://www.youtube.com/watch?v=r3mzaElL8nA',
          label: 'Abrir vídeo de cadastro',
          external: true,
        },
      },
      {
        title: 'Vídeo de apoio: consulta de pets',
        text: 'Também havia suporte visual para consulta e leitura do histórico dentro da rotina clínica.',
        bullets: ['Consulta rápida do histórico', 'Leitura prática do prontuário'],
        link: {
          href: 'https://www.youtube.com/watch?v=0QWy9SOUZLY',
          label: 'Abrir vídeo de consulta',
          external: true,
        },
      },
      {
        title: 'Vacinas e protocolos',
        text: 'O produto reforça o acompanhamento de vacinas e as próximas doses como parte da gestão inteligente do relacionamento.',
        bullets: ['Protocolos por pet', 'Avisos e comunicação recorrente'],
      },
      {
        title: 'Operação de balcão',
        text: 'O SEUPDV aparece como apoio importante para acelerar atendimento comercial e manter sincronismo com estoque e financeiro.',
        bullets: ['PDV offline', 'NFCe mesmo sem internet'],
        link: {
          href: 'https://www.youtube.com/watch?v=qCQaLNfMtZc',
          label: 'Abrir vídeo do SEUPDV',
          external: true,
        },
      },
    ],
  },
  notice: {
    title: 'Operação fiscal',
    text: 'Para emissão de documentos fiscais, a operação do ECOSIS Pet exige certificado digital A1 no formato .pfx.',
    badges: ['Certificado A1 (.pfx)'],
  },
  contact: {
    tag: 'Contato',
    title: 'Quer avaliar o ECOSIS Pet para a sua rotina de atendimento?',
    lead: 'A equipe pode analisar o fluxo clínico e comercial do seu negócio e indicar a melhor composição da implantação.',
  },
};

function EcosisPet() {
  return <ProductPageTemplate data={data} />;
}

export default EcosisPet;
